import React, { forwardRef, useEffect, useMemo, useRef } from 'react';
import { generateInputStyles } from 'shared-components/Input/InputStyles';
import { useTheme } from '@emotion/react';
import { Box } from '@mui/material';

interface Props {
    placeholder?: string;
    className?: string;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    id?: string;
}

export const Input = forwardRef<HTMLInputElement, Props>(
    ({ placeholder = '', className = '', value = '', onChange = () => {}, id = '' }: Props, ref) => {
        const theme = useTheme();
        const styles = useMemo(() => generateInputStyles(theme), [theme]);

        const inputRef = useRef<HTMLInputElement | null>(null);

        useEffect(() => {
            const handleMessage = (event: MessageEvent) => {
                if (event.data && event.data.type === 'triggerAutofocus' && inputRef.current) {
                    inputRef.current.focus();
                }
            };

            window.addEventListener('message', handleMessage);

            return () => {
                window.removeEventListener('message', handleMessage);
            };
        }, []);

        return (
            <Box
                component="input"
                type="text"
                id={id}
                className={className}
                sx={styles.input}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                ref={(instance:any) => {
                    inputRef.current = instance;
                    if (typeof ref === 'function') {
                        ref(instance);
                    } else if (ref) {
                        (ref as React.MutableRefObject<HTMLInputElement | null>).current = instance;
                    }
                }}
            />
        );
    },
);

Input.displayName = 'Input';
